import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import { ArrowGlyph, Stage, useOpenAgentDemo } from './primitives';

/**
 * `title` replaces the headline where "customers" is the wrong word, and
 * `exchange` the sample chat beside it, so each industry page shows a
 * question its own visitors would ask.
 */
export default function AgentFinalCta({
  title,
  exchange,
}: {
  title?: string;
  exchange?: { question: string; answer: string };
}) {
  const { t } = useLocale('chatbots');
  const openDemo = useOpenAgentDemo();

  return (
    <section className='mx-auto max-w-7xl px-3 pb-24 pt-8 sm:px-6 sm:pb-32 lg:px-8'>
      <Stage
        variant='indigo'
        halftoneAt='100% 100%'
        className='px-6 py-16 sm:px-14 sm:py-24'
      >
        <div className='relative grid items-center gap-14 lg:grid-cols-[1.7fr_1fr]'>
          <div>
            <h2
              className={clsxm(
                styles.display,
                'text-[2.25rem] font-semibold leading-[1.06] text-white sm:text-5xl lg:text-[3.5rem]',
              )}
            >
              {title ??
                t('Your agent could be answering customers by lunchtime.')}
            </h2>
            <p className='mt-6 max-w-lg text-lg leading-8 text-primary-100'>
              {t('Free to start. No credit card, no code.')}
            </p>
            <div className='mt-10 flex flex-wrap items-center gap-3'>
              <Link
                href='/login?preset=bots'
                className='inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-primary-700 shadow-[0_10px_30px_-10px_rgba(20,10,80,0.6)] transition-colors duration-200 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {t('Build your agent free')}
                <ArrowGlyph />
              </Link>
              <button
                type='button'
                onClick={openDemo}
                className='inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold text-white ring-1 ring-inset ring-white/35 transition duration-200 hover:bg-white/10 hover:ring-white/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {t('Talk to our agent')}
              </button>
            </div>
          </div>

          <div aria-hidden='true' className='hidden lg:block'>
            <div className={clsxm(styles.float, 'ms-auto max-w-sm space-y-3')}>
              <p className='ms-auto w-fit max-w-[80%] rounded-2xl rounded-ee-md bg-white/15 px-4 py-2.5 text-[15px] text-white ring-1 ring-inset ring-white/20'>
                {exchange?.question ?? t('Are you open this Sunday?')}
              </p>
              <p className='w-fit max-w-[85%] rounded-2xl rounded-es-md bg-white px-4 py-2.5 text-[15px] text-[#10101c] shadow-[0_20px_40px_-20px_rgba(20,10,80,0.6)]'>
                {exchange?.answer ??
                  t('Yes, from 9 to 5. Want me to save you a spot?')}
              </p>
              <p className='flex items-center gap-2 ps-1 text-xs font-medium text-primary-100'>
                <span className='h-1.5 w-1.5 rounded-full bg-emerald-400' />
                {t('Answered in 2 seconds, at 11:48 p.m.')}
              </p>
            </div>
          </div>
        </div>
      </Stage>
    </section>
  );
}
